import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiGet } from '../../api/apiClient';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Badge from 'react-bootstrap/Badge'; // Import Badge component
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton'; // Import RTButton for the Add Room button
import Beds from './Beds'; // Import the Beds component
import { formatRoomGender, getRoomGenderBadgeClassName } from '../../utils/occupancyConstants';
import './RoomList.css';

const RoomsList = ({ buildingId, buildingName, refresh, buildingStatus, onAddRoom, onEditRoom }) => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [selectedRoomNumber, setSelectedRoomNumber] = useState(null); // State to track the selected room

  const fetchRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet(`/room/list/${buildingId}`);
      setRooms(res.data || []);
    } catch {
      setRooms([]);
    }
    setLoading(false);
  }, [buildingId]);

  const handleRoomSelect = useCallback((room) => {
    setSelectedRoom(room);
    setSelectedRoomNumber(room.room_number || null);
  }, []);

  const handleBedsChanged = useCallback(async () => {
    const selectedRoomId = selectedRoom?.room_id;
    try {
      const res = await apiGet(`/room/list/${buildingId}`);
      const updatedRooms = res.data || [];
      setRooms(updatedRooms);
      if (selectedRoomId) {
        const updatedRoom = updatedRooms.find((room) => room.room_id === selectedRoomId);
        if (updatedRoom) {
          setSelectedRoom(updatedRoom);
          setSelectedRoomNumber(updatedRoom.room_number || null);
        }
      }
    } catch {
      // Keep existing room list on refresh failure
    }
  }, [buildingId, selectedRoom?.room_id]);

  useEffect(() => {
    fetchRooms();
    // Reset selected room when building changes
    setSelectedRoom(null);
    setSelectedRoomNumber(null);
    // eslint-disable-next-line
  }, [buildingId, refresh, fetchRooms]);

  const selectedRoomName = useMemo(() => {
    if (!selectedRoom) return '';
    return selectedRoom.room_name || '';
  }, [selectedRoom]);

  // Keep menu/header visible — spinner stays in the rooms panel only
  if (loading && rooms.length === 0) {
    return <RTSpinner message="Loading rooms..." />;
  }

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Left Half: Room List */}
      <div
        style={{
          flex: 1,
          maxHeight: '500px', // Set max height
          overflowY: 'auto', // Enable scrolling
          padding: '10px',
          borderRight: '1px solid #ddd',
          borderBottom: '1px solid #ddd',
        }}
      >
        {/* Sticky Add Room Button */}
        {buildingStatus === 'Active' && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center', // Align horizontally in the center
              alignItems: 'center', // Align vertically in the center
              position: 'sticky',
              top: 0,
              marginBottom: '20px',
              zIndex: 10, // Ensure it stays above other content
              gap: '10px', // Add gap between buttons
            }}
          >
            <RTButton
              variant="secondary"
              permissionKey="building.add"
              onClick={onAddRoom}
            >
              Add Room
            </RTButton>
            {selectedRoom && onEditRoom && (
              <RTButton
                variant="secondary"
                permissionKey="building.add"
                onClick={() => onEditRoom(selectedRoom)}
              >
                Edit Room
              </RTButton>
            )}
          </div>
        )}

        {/* Room Cards */}
        <Row xs={1} className="g-4">
          {rooms.map((room, index) => (
            <Col key={room.room_id}>
              <Card
                onClick={() => handleRoomSelect(room)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: '#e3eafc', // Highlight selected room
                  border: selectedRoom && selectedRoom.room_id === room.room_id ? '2px solid #007bff' : '1px solid #ddd', // Add border for selected room
                  transition: 'background-color 0.15s ease, border-color 0.15s ease', // Faster, more specific transition
                }}
              >
                <Card.Body>
                  <Card.Title>
                    {room.room_name}
                    {room.occupancy_gender && (
                      <span className={getRoomGenderBadgeClassName(room.occupancy_gender)}>
                        {formatRoomGender(room.occupancy_gender)}
                      </span>
                    )}
                  </Card.Title>
                  <Card.Text style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Badge bg="info" style={{ fontSize: '14px' }}>
                      Vacant: {room.beds.filter(bed => bed.is_vacant).length} / {room.beds.length}
                    </Badge>
                    <span style={{ fontSize: '18px', color: 'grey' }}> {/* Changed text color to blue */}
                      <strong>₹ {room.bed_rent ? parseFloat(room.bed_rent).toFixed(2) : '0.00'}</strong> {/* Ensure bed_rent is a number */}
                    </span>
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
          {rooms.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px' }}>No rooms found.</div>
          )}
        </Row>
      </div>

      {/* Right Half: Beds Component */}
      <div style={{ flex: 2, padding: '10px' }}>
        {selectedRoom ? (
          <Beds 
            room={selectedRoom} 
            roomNumber={selectedRoomNumber}
            buildingName={buildingName}
            roomName={selectedRoomName}
            onBedsChanged={handleBedsChanged}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '30px' }}>Select a room to view details.</div>
        )}
      </div>
    </div>
  );
};

export default RoomsList;